import type { EditorStore, Selection } from './editor-store';
import { validate } from './editor-validation';
import type { History } from './editor-history';
import { addSegment } from './editor-commands';
import type { LevelSegment } from '@/game/level';

export class EditorOutline {
  private host: HTMLElement;
  private store: EditorStore;
  private history: History;

  constructor(host: HTMLElement, store: EditorStore, history: History) {
    this.host = host;
    this.store = store;
    this.history = history;
    this.render();
    store.on('level-changed', () => this.render());
    store.on('selection-changed', () => this.render());
  }

  private render(): void {
    const lvl = this.store.level;
    const sel = this.store.selection;
    const rows: string[] = [];

    // Build warning badge map
    const issues = validate(lvl);
    const byKey = new Map<string, number>();
    for (const i of [...issues.blocking, ...issues.warnings]) {
      const k = JSON.stringify(i.jumpTo);
      byKey.set(k, (byKey.get(k) ?? 0) + 1);
    }

    rows.push(this.row('⚙ Settings', { kind: 'settings' }, sel, byKey));
    rows.push(this.row('ℹ Metadata', { kind: 'meta' }, sel, byKey));

    for (const seg of lvl.segments) {
      rows.push(this.row(`▼ ${seg.id} · ${seg.mode}`, { kind: 'segment', segmentId: seg.id }, sel, byKey));
      rows.push(`<div class="indent">`);
      rows.push(this.row('Terrain', { kind: 'terrain', segmentId: seg.id }, sel, byKey));
      rows.push(`<div class="group-label">Obstacles (${seg.obstacles.length})${seg.obstacles.length === 0 ? ' — tap 🔺 to place' : ''}</div>`);
      for (let i = 0; i < seg.obstacles.length; i++) {
        const o = seg.obstacles[i];
        rows.push(this.row(
          `${iconForObstacle(o.type)} ${o.type} @ ${Math.round(o.position.along)}`,
          { kind: 'obstacle', segmentId: seg.id, index: i }, sel, byKey,
        ));
      }
      rows.push(`<div class="group-label">Collectibles (${lvl.collectibles.length})${lvl.collectibles.length === 0 ? ' — tap ⭐ to place' : ''}</div>`);
      for (let i = 0; i < lvl.collectibles.length; i++) {
        const c = lvl.collectibles[i];
        rows.push(this.row(
          `⭐ ${c.type} @ ${Math.round(c.position.along)}`,
          { kind: 'collectible', index: i }, sel, byKey,
        ));
      }
      const decorations = seg.decorations ?? [];
      rows.push(`<div class="group-label">Decorations (${decorations.length})</div>`);
      for (let i = 0; i < decorations.length; i++) {
        const d = decorations[i];
        rows.push(this.row(
          `✨ ${d.type} @ ${Math.round(d.position.along)}`,
          { kind: 'decoration', segmentId: seg.id, index: i }, sel, byKey,
        ));
      }
      rows.push(`</div>`);
    }

    const triggers = lvl.triggers ?? [];
    rows.push(`<div class="group-label">TRIGGERS (${triggers.length})${triggers.length === 0 ? ' — tap a trigger palette button to add' : ''}</div>`);
    for (const t of triggers) {
      rows.push(this.row(
        `${triggerIcon(t.type)} ${t.type} @ ${Math.round(t.position)}`,
        { kind: 'trigger', id: t.id }, sel, byKey,
      ));
    }

    rows.push(`<div class="outline-row add-seg" data-act="add-segment">➕ Add segment</div>`);

    this.host.innerHTML = `
      <div class="outline-root">
        <div class="group-label">OUTLINE</div>
        ${rows.join('')}
      </div>
    `;

    this.host.querySelectorAll<HTMLElement>('[data-sel]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        const raw = el.dataset['sel']!;
        this.store.setSelection(JSON.parse(raw));
      });
    });

    this.host.querySelectorAll<HTMLElement>('[data-sel]').forEach((el) => {
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const raw = el.dataset['sel']!;
          this.store.setSelection(JSON.parse(raw));
        }
      });
    });

    this.host.querySelector('[data-act="add-segment"]')?.addEventListener('click', (e) => {
      e.stopPropagation();
      const newId = `seg-${crypto.randomUUID().slice(0, 6)}`;
      const newSeg: LevelSegment = {
        id: newId, mode: 'real', length: 1000, speed: 1,
        terrain: { type: 'flat', floor: { function: 'constant', offset: 100 }, ceiling: null },
        obstacles: [], decorations: [],
      };
      this.history.execute(addSegment(this.store, newSeg));
      this.store.setSelection({ kind: 'segment', segmentId: newId });
    });
  }

  private row(label: string, sel: Selection, current: Selection, byKey?: Map<string, number>): string {
    const active = selectionsEqual(sel, current);
    const badge = byKey?.get(JSON.stringify(sel)) ? ' <span class="warn">⚠</span>' : '';
    const selectedAttr = active ? ' aria-selected="true"' : ' aria-selected="false"';
    return `<div class="outline-row${active ? ' selected' : ''}" role="button" tabindex="0"${selectedAttr} data-sel='${JSON.stringify(sel)}'>${escapeHtml(label)}${badge}</div>`;
  }
}

function triggerIcon(kind: string): string {
  switch (kind) {
    case 'text': return '💬';
    case 'color': return '🎨';
    case 'speed': return '💨';
    case 'camera': return '📷';
    case 'music': return '🎵';
    case 'gravity': return '⬆';
    case 'player_mode': return '👤';
    case 'spawn': return '✨';
    case 'mode': return '🌀';
    case 'fog':  return '🌫';
    default: return '⚡';
  }
}

function iconForObstacle(t: string): string {
  switch (t) {
    case 'spike': return '🔺';
    case 'wall': return '🧱';
    default: return '◻';
  }
}

function selectionsEqual(a: Selection, b: Selection): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]!));
}
